import { useState, useEffect } from 'react';

interface TutorialStep {
  id: string;
  text: string;
  subtext?: string;
  position: 'center' | 'top-center' | 'bottom-center' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  targetSelector?: string; // CSS selector of the DOM element to spotlight
}

const STEPS: TutorialStep[] = [
  {
    id: 'run_welcome',
    text: "Welcome to Milan's Underground Map!",
    subtext:
      "You've successfully launched a heist operation. " +
      "This is your tactical map where you will navigate different sectors of Milan.",
    position: 'center',
  },
  {
    id: 'run_decks',
    text: 'Your Active Decks',
    subtext:
      "For this run, you carry two active decks (Deck A & Deck B). " +
      "Before taking on a sector, you'll choose one to play with. " +
      "Warning: any card the opponent snatches from your deck in a match is lost permanently! " +
      "A deck that loses even one card becomes invalid (it no longer has 14 cards). " +
      "If both of your decks become invalid, the run ends in a Forced Exit.",
    position: 'bottom-center',
    targetSelector: '.run-decks-panel',
  },
  {
    id: 'run_map',
    text: 'Traversing Heist Sectors',
    subtext:
      "Plan your route carefully! Starting at the bottom, click any adjacent node to inspect it and start a heist match. " +
      "Solid lines show pathways you can travel. " +
      "Nodes have different difficulty tiers: Easy, Medium, Hard, and Boss.",
    position: 'top-center',
    targetSelector: '.run-map-panel',
  },
  {
    id: 'run_extract',
    text: 'Extraction Safehouses ($)',
    subtext:
      "Nodes marked with a '$' are safehouses. " +
      "Reaching them lets you extract successfully to bank all accumulated loot to your permanent stash, " +
      "or reconfigure your active decks by swapping cards between Deck A, Deck B and the loot gathered in this run.",
    position: 'top-center',
    targetSelector: '.run-map-panel',
  },
  {
    id: 'run_loot',
    text: 'Run Loot & Risk',
    subtext:
      "All cards snatched and resources captured during matches are stored in your loot bag. " +
      "They only become yours permanently once you extract successfully. " +
      "If you get a Forced Exit, all accumulated loot is lost!",
    position: 'top-right',
    targetSelector: '.run-loot-sidebar',
  },
];

interface RunTutorialOverlayProps {
  onDismiss: () => void;
}

export function RunTutorialOverlay({ onDismiss }: RunTutorialOverlayProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [spotlightBounds, setSpotlightBounds] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const currentStep = STEPS[stepIndex];

  // Calculate and update bounds of target element to highlight
  useEffect(() => {
    const updateBounds = () => {
      if (!currentStep?.targetSelector) {
        setSpotlightBounds(null);
        return;
      }
      const el = document.querySelector(currentStep.targetSelector);
      if (el) {
        const rect = el.getBoundingClientRect();
        const padding = 8;
        setSpotlightBounds({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        });
      } else {
        setSpotlightBounds(null);
      }
    };

    updateBounds();
    const timer = setTimeout(updateBounds, 120); // slight delay to allow layout animations to settle

    window.addEventListener('resize', updateBounds);
    window.addEventListener('scroll', updateBounds, { capture: true, passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateBounds);
      window.removeEventListener('scroll', updateBounds, { capture: true });
    };
  }, [stepIndex, currentStep]);

  if (!currentStep) return null;

  const isLast = stepIndex >= STEPS.length - 1;

  // Compute container positioning style based on step configuration
  let containerStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 8000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
    width: '100vw',
    transition: 'all 0.3s ease-in-out',
  };

  switch (currentStep.position) {
    case 'center':
      containerStyle = {
        ...containerStyle,
        inset: 0,
        justifyContent: 'center',
      };
      break;
    case 'top-center':
      containerStyle = {
        ...containerStyle,
        top: '120px',
        left: '50%',
        transform: 'translateX(-50%)',
      };
      break;
    case 'bottom-center':
      containerStyle = {
        ...containerStyle,
        bottom: '220px',
        left: '50%',
        transform: 'translateX(-50%)',
      };
      break;
    case 'bottom-left':
      containerStyle = {
        ...containerStyle,
        bottom: '160px',
        left: '20px',
        alignItems: 'flex-start',
        width: 'auto',
      };
      break;
    case 'bottom-right':
      containerStyle = {
        ...containerStyle,
        bottom: '160px',
        right: '20px',
        alignItems: 'flex-end',
        width: 'auto',
      };
      break;
    case 'top-left':
      containerStyle = {
        ...containerStyle,
        top: '70px',
        left: '20px',
        alignItems: 'flex-start',
        width: 'auto',
      };
      break;
    case 'top-right':
      containerStyle = {
        ...containerStyle,
        top: '150px',
        right: '20px',
        alignItems: 'flex-end',
        width: 'auto',
      };
      break;
  }

  return (
    <>
      {/* Background Dim / Spotlight Overlay */}
      {spotlightBounds ? (
        <>
          {/* Top overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: Math.max(0, spotlightBounds.top),
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 7500,
              pointerEvents: 'auto',
              transition: 'all 0.25s ease-in-out',
            }}
          />
          {/* Bottom overlay */}
          <div
            style={{
              position: 'fixed',
              top: spotlightBounds.top + spotlightBounds.height,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 7500,
              pointerEvents: 'auto',
              transition: 'all 0.25s ease-in-out',
            }}
          />
          {/* Left overlay */}
          <div
            style={{
              position: 'fixed',
              top: spotlightBounds.top,
              left: 0,
              width: Math.max(0, spotlightBounds.left),
              height: spotlightBounds.height,
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 7500,
              pointerEvents: 'auto',
              transition: 'all 0.25s ease-in-out',
            }}
          />
          {/* Right overlay */}
          <div
            style={{
              position: 'fixed',
              top: spotlightBounds.top,
              left: spotlightBounds.left + spotlightBounds.width,
              right: 0,
              height: spotlightBounds.height,
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 7500,
              pointerEvents: 'auto',
              transition: 'all 0.25s ease-in-out',
            }}
          />
          {/* Highlight border and glow around target */}
          <div
            style={{
              position: 'fixed',
              top: spotlightBounds.top,
              left: spotlightBounds.left,
              width: spotlightBounds.width,
              height: spotlightBounds.height,
              borderRadius: 8,
              border: '2px solid var(--accent-color)',
              boxShadow: '0 0 20px var(--accent-color), inset 0 0 10px rgba(255, 255, 255, 0.1)',
              pointerEvents: 'none',
              zIndex: 7501,
              transition: 'all 0.25s ease-in-out',
            }}
          />
        </>
      ) : (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 7500,
            background: 'rgba(0, 0, 0, 0.65)',
            pointerEvents: 'auto',
          }}
        />
      )}

      {/* Tutorial Popup Box */}
      <div style={containerStyle}>
        <div
          style={{
            pointerEvents: 'all',
            background: '#0c0c18',
            border: '2px solid var(--accent-color)',
            borderRadius: 10,
            padding: '18px 24px',
            maxWidth: 480,
            width: '92vw',
            boxShadow: '0 4px 32px rgba(0,0,0,0.7)',
            position: 'relative',
          }}
        >
          {/* Step counter */}
          <div style={{ position: 'absolute', top: 10, right: 14, fontSize: 10, color: '#666' }}>
            {stepIndex + 1} / {STEPS.length}
          </div>

          {/* Content */}
          <div style={{ fontWeight: 'bold', color: 'var(--accent-color)', fontSize: 15, marginBottom: 6 }}>
            {currentStep.text}
          </div>
          {currentStep.subtext && (
            <div style={{ color: '#ccc', fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
              {currentStep.subtext}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
            <button
              onClick={onDismiss}
              style={{
                background: 'none', border: '1px solid #444', color: '#777',
                padding: '5px 12px', borderRadius: 4, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 11,
              }}
            >
              Skip guide
            </button>
            <button
              onClick={() => {
                if (isLast) { onDismiss(); } else { setStepIndex(i => i + 1); }
              }}
              className="multi-btn"
              style={{ fontSize: 12, padding: '6px 16px' }}
            >
              {isLast ? 'Got it!' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
